import {
	Vue, Component, Ref, toNative,
} from 'vue-facing-decorator';
import Swal from 'sweetalert2';
import * as DB from 'interfaces/database';
import { BModal } from 'bootstrap-vue';
import mitt from 'mitt';
import { httpClient } from 'utils/http';
import ColoredBox from 'components/Tabulator/ColoredBox';
import { TabEvent } from 'interfaces/app';
import { CellComponentExtended, ColumnDefinitionExtended, TabulatorFull as Tabulator } from 'tabulator-tables';
import { createInstance } from 'utils/vue';
import TabulatorBtn from 'components/Tabulator/TabulatorBtn';
import Template from './template.vue';

const eventBus = mitt<TabEvent<DB.PageBackgroundModel>>();
@Component({
	components: {
		TabulatorBtn,
		ColoredBox,
	},
	emits: ['routePagebg'],
	mixins: [Template],
})
class PageBgTable extends Vue {
	@Ref('addpagebg-modal')
	private readonly modal!: BModal;

	@Ref('pagebg')
	private readonly tableReference!: HTMLDivElement;

	private name = '';

	private isLoaded = false;

	private columnDefs: ColumnDefinitionExtended[] = [];

	private table?: Tabulator;

	private get loggedIn(): boolean {
		return this.$auth0.isAuthenticated.value;
	}

	protected beforeMount(): void {
		this.columnDefs = [
			{
				field: 'id',
				title: 'ID',
			},
			{
				title: 'Name',
				field: 'name',
			},
			{
				title: 'Color',
				field: 'color',
				formatter: (cell: CellComponentExtended<DB.PageBackgroundModel>) => {
					const instance = createInstance({
						component: ColoredBox,
						props: {
							data: cell.getData(),
						},
					});
					return instance;
				},
			},
			{
				title: 'Image',
				field: 'image',
				formatter: 'image',
				formatterParams: {
					height: 150,
					width: 150,
				},
			},
			{
				title: 'Actions',
				formatter: (cell: CellComponentExtended<DB.PageBackgroundModel>) => {
					const instance = createInstance({
						component: TabulatorBtn,
						props: {
							data: cell.getData(),
							buttons: [
								{
									id: 'edit',
									eventName: 'editItem',
									className: 'fa-edit',
								},
								{
									id: 'delete',
									eventName: 'deleteItem',
									className: 'fa-times',
								},
							],
							eventBus,
						},
					});
					return instance;
				},
			},
		] as ColumnDefinitionExtended[];
	}

	protected mounted(): void {
		eventBus.on(
			'deleteItem',
			this.deletePageBg,
		);
		eventBus.on(
			'editItem',
			this.editPageBg,
		);
		this.tableInitialization();
		this.table?.on(
			'tableBuilt',
			this.onTableBuilt,
		);
	}

	protected beforeUnmount(): void {
		eventBus.off(
			'deleteItem',
			this.deletePageBg,
		);
		eventBus.off(
			'editItem',
			this.editPageBg,
		);
		this.table?.off(
			'tableBuilt',
			this.onTableBuilt,
		);
		this.table?.destroy();
	}

	private onTableBuilt(): void {
		this.getData();
	}

	private tableInitialization(): void {
		this.table = new Tabulator(
			this.tableReference,
			{
				height: '60vh',
				layout: 'fitColumns',
				columns: this.columnDefs,
			},
		);
	}

	private async getData(): Promise<void> {
		const parameter = new URLSearchParams({
			orderby: 'serialnumber ASC',
			limit: '0',
		});
		this.table?.alert('Loading');
		try {
			const { data } = await httpClient.get<DB.PageBackgroundModel[]>(`/api/pagebg?${parameter}`);
			this.table?.setData(data);
		} catch (err: any) {
			this.$toastError(err.message);
		} finally {
			this.table?.clearAlert();
		}
	}

	protected addPagebg(): void {
		this.isLoaded = true;
		httpClient.post(
			'/api/pagebg',
			{ name: this.name },
		).then((res) => {
			this.modal.hide();

			this.name = '';
			this.$emit(
				'routePagebg',
				res.data.id,
			);
			return undefined;
		})
			.finally(() => {
				this.isLoaded = false;
			})
			.catch((err) => {
				this.$toastError(err.message);
			});
	}

	private editPageBg(data: TabEvent<DB.PageBackgroundModel>['editItem']): void {
		this.$emit(
			'routePagebg',
			data.id,
		);
	}

	private deletePageBg(data: TabEvent<DB.PageBackgroundModel>['deleteItem']): void {
		Swal.fire({
			title: 'Are you sure?',
			text: 'You will not be able to recover this file!',
			icon: 'warning',
			showCancelButton: true,
			customClass: {
				confirmButton: 'btn btn-danger m-1',
				cancelButton: 'btn btn-secondary m-1',
			},
			confirmButtonText: 'Yes, delete it!',
			html: false,
		}).then((result) => {
			if (result.value) {
				this.table?.alert('Deleting...');
				// eslint-disable-next-line promise/no-nesting
				httpClient.delete(`/api/pagebg/${data.id}`).then(() => {
					this.$toastSuccess('Item Deleted');
					this.table?.deleteRow(data.id);
					return null;
				}).finally(() => {
					this.table?.clearAlert();
				}).catch((err) => {
					this.$toastError(err.message);
				});
			}
			return undefined;
		}).catch((err) => {
			this.$toastError(err.message);
		});
	}
}

export default toNative(PageBgTable);
